import Hero, { tabs } from './hero';
import React, { ComponentType, useEffect, useRef, useState } from 'react';
import { Button, Input, message } from 'antd';
import { cn, isInsUserUrl } from '@/lib/utils';
import WhyChoose from '@/components/email-finder/WhyChoose';
import StepCard from '@/components/email-finder/StepCard';
import KeyFeatures from '@/components/email-finder/KeyFeatures';
import ChooseContent from '@/components/email-finder/ChooseContent';
import WhoBenefits from '@/components/email-finder/WhoBenefits';
import FAQContent from '@/components/email-finder/FAQContent';
import PricingContent from './PricingContent';
import BestTipsContent from '../LandingPage/BestTipsContent';
import DownloadStoriesBottomContent from '../LandingPage/DownloadStoriesBottomContent';
import useRegisterOrLoginGuide from '@/lib/hooks/common/useRegisterOrLoginGuide';
import OptModal from './OptModal';
import { getEmailFinderResult, getInsEmailFinderCreditInfo, getInsEmailFinderData } from '@/lib/service/dashboard';
import { useUserProvider } from '@/context/UserProvider';
import ResultPeal from './result-peal';
import { useTranslation } from 'next-i18next';
import CommonSimpleModal from '@/components/Common/CommonSimpleModal';
import dynamic from 'next/dynamic';
import { InsAccountModel } from '@/types/home';
import { ga_logEvent } from '@/shared/ga';
import { fetchInsMonitorSearch } from '@/lib/service/home';
import { useRouter } from 'next/router';
import InputSelect from '@/components/email-finder/InputSelect';
import { InstagramOutlined } from '@ant-design/icons';
import AIDiscover, { PresetPrompt } from '@/components/email-finder/AIDiscover';

const UserDashboard = dynamic(() => import('@/components/Dashboard/UserDashboard'), { ssr: false });

export type ITab = {
  tab: 'hashtag' | 'keyword' | 'location' | 'profile' | 'followers' | 'followings' | 'ai-discover';
  label: 'hashtag' | 'keyword' | 'location' | 'profile' | 'follower' | 'following' | 'ai-discover';
  title: string;
  Icon: ComponentType<{ className?: string; size?: number; color?: string }>;
  modalTitle: string;
  modalDesc: string;
  placeholder: string;
  tagName: string;
  tagBuyName: string;
  tooltip: string;
};

export enum pageTypeEnum {
  syntheticPage = '1', // 综合页 - 二级路由
  subPage = '2', // 子页 - 三级路由
}

interface PageData {
  data: any;
  type: pageTypeEnum;
}

export default function PageClient({ data, type }: PageData) {
  const { t } = useTranslation('common');
  const router = useRouter();

  const {
    contentData: {
      contentInfo: {
        hero: sectionHero,
        'section-why-choose': sectionWhyChoose,
        'section-why-choose-type': sectionWhyChooseType,
        'section-steps': sectionSteps,
        'section-all-in-one': sectionAllInOne,
        'section-who-benefits': sectionWhoBenefits,
        'section-pricing': sectionPricing,
        'section-faq': sectionFAQ,
        'section-top-blog': sectionTopBlog,
        'section-blog': sectionBlog,
        'section-buttom': sectionButtom,
      } = {},
      faqList,
      topBlogList,
      blogList,
    },
    id: defaultTab,
    emailFinderAIPrompts,
  } = data;
  const { onOpenLoginModal } = useUserProvider();
  const [currentTab, setCurrentTab] = useState<ITab['tab']>(defaultTab ?? 'followers');
  // 轮询定时器引用
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // 现在tab基本信息
  const curTab = tabs.find((tab) => tab.tab === currentTab);
  const { guideToRegisterOrLogin, isLogin } = useRegisterOrLoginGuide();
  //确认弹窗
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  //用户信息
  const [userInfo, setUserInfo] = useState<any>(null);

  useEffect(() => {
    if (router.pathname === '/instagram-email-finder') {
      const sessionTab = sessionStorage.getItem('ins_email_finder_tab');
      if (sessionTab) {
        setCurrentTab(sessionTab as ITab['tab']);
        setResult(null);
        setSelectedLocationOrHashtag(null);
        searchId.current = '';
        setTimeout(() => {
          sessionStorage.removeItem('ins_email_finder_tab');
        }, 100);
      }
    } else if (defaultTab) {
      setCurrentTab(defaultTab);
      setResult(null);
      setSelectedLocationOrHashtag(null);
      searchId.current = '';
    }
  }, [defaultTab, router.pathname]);

  // 点击开始查询
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  // 输入内容
  const [inputValue, setInputValue] = useState('');
  // 获取用户积分信息
  const [userCreditInfo, setUserCreditInfo] = useState<any>(null);
  const [userName, setUserName] = useState<string>('');
  const [openPrivateAccount, setOpenPrivateAccount] = useState(null);
  // 保存选中的 location/hashtag 信息
  const [selectedLocationOrHashtag, setSelectedLocationOrHashtag] = useState<{
    pk: string;
    name: string;
  } | null>(null);

  const searchId = useRef<string>('');
  const searchTabRef = useRef<ITab['tab'] | null>(null);
  // AI Discover 任务 ID
  const [aiDiscoverTaskId, setAiDiscoverTaskId] = useState<number | null>(null);
  // AI Discover 初始任务 ID（用于恢复状态）
  const [aiDiscoverInitialTaskId, setAiDiscoverInitialTaskId] = useState<number | null>(null);

  // 当 AI Discover 任务 ID 变化时，保存到 sessionStorage
  useEffect(() => {
    if (aiDiscoverTaskId) {
      sessionStorage.setItem('ai_discover_task_id', aiDiscoverTaskId.toString());
    } else {
      sessionStorage.removeItem('ai_discover_task_id');
    }
  }, [aiDiscoverTaskId]);

  // 从 URL 参数恢复 AI discover 状态
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    const taskIdParam = urlParams.get('task_id');

    if (tabParam === 'ai-discover' && taskIdParam) {
      const taskId = parseInt(taskIdParam, 10);
      if (!isNaN(taskId)) {
        setCurrentTab('ai-discover');
        setAiDiscoverInitialTaskId(taskId);
        setAiDiscoverTaskId(taskId);

        // 从支付页面返回，需要重新获取用户订阅信息
        if (isLogin) {
          getUserCreditInfo();
        }

        // 清除 URL 参数
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [isLogin]);

  useEffect(() => {
    if (isLogin) {
      getUserCreditInfo();
    }
  }, [isLogin]);

  const getUserCreditInfo = async () => {
    const res = await getInsEmailFinderCreditInfo();
    if (res.code == 0) {
      setUserCreditInfo(res.data);
    }
  };

  const accountSearchRequest = async (accountName?: string) => {
    if (!accountName) {
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const res = await fetchInsMonitorSearch(accountName, router.locale, {
        isPremium: true,
        bizFuncName: 'email_finder',
      });
      setLoading(false);

      if (res.code !== 0 || !res.data?.length) {
        setOpenPrivateAccount({
          title: t('Invalid Input'),
          desc: t('The content you entered is invalid. It may be in the wrong format, empty, or does not exist.'),
          btnTitle: t('Try Again'),
        });
        return;
      }

      const account =
        res.data.find(
          (item) => item.media_name === accountName || item.media_name === accountName?.replace(/^@/, '')
        ) || res.data[0];

      if (!account) {
        setOpenPrivateAccount({
          title: t('Invalid Input'),
          desc: t('The content you entered is invalid. It may be in the wrong format, empty, or does not exist.'),
          btnTitle: t('Try Again'),
        });
        return;
      }

      if (account.is_private === 1) {
        setOpenPrivateAccount({
          title: t('OopsPrivateProfileDetected'),
          desc: (
            <>
              <div style={{ marginBottom: '24px', textAlign: 'center' }}>{t('ThisInstagramAccountIsPrivate_Desc')}</div>
              <UserDashboard is_private={1} profile_pic_url={account.profile_pic_url} media_name={account.media_name} />
            </>
          ),
          btnTitle: t('TRYAGAIN'),
          btnDesc: (
            <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
              {t('Note: Only public accounts can be tracked.')}
            </p>
          ),
        });
        return;
      }

      if (account.is_private === 2) {
        setOpenPrivateAccount({
          title: t('Invalid Input'),
          desc: t('The content you entered is invalid. It may be in the wrong format, empty, or does not exist.'),
          btnTitle: t('Try Again'),
        });
        return;
      }
      setUserName(account.media_name);
      setUserInfo({
        media_name: account.media_name,
        follower_count: account.follower_count,
        following_count: account.following_count,
        profile_pic_url: account.profile_pic_url,
      });
      setOpenConfirmModal(true);
    } catch (error: any) {
      setLoading(false);
      message.error(error?.message || 'Something went wrong, please try again later.');
    }
  };

  // 确认按钮点击
  const handleBtnClick = () => {
    if (inputValue.trim().length == 0) {
      message.warning('Please enter a value');
      return;
    }
    const formattedName = isInsUserUrl(inputValue);
    if (!formattedName) {
      message.warning('Please enter a valid userName or profileURL');
      return;
    }
    setUserName(formattedName);
    if (!isLogin) {
      onOpenLoginModal(true);
      return;
    }
    if (curTab?.tab == 'followings' || curTab?.tab == 'followers') {
      accountSearchRequest(formattedName);
      return;
    }
    setOpenConfirmModal(true);
  };

  const [animateLoading, setAnimateLoading] = useState(false);

  const handleConfirm = (emailValidation: boolean, profileCount: number) => {
    searchTabRef.current = curTab.tab;
    setLoading(true);
    setResult(null);
    setOpenConfirmModal(false);
    // 埋点
    ga_logEvent(curTab.tagName);

    // 立即显示 searching 动画
    setAnimateLoading(true);

    // 根据当前 tab 类型决定使用哪个值作为 info
    const infoValue =
      curTab?.tab === 'location' || curTab?.tab === 'hashtag' ? selectedLocationOrHashtag?.pk : userName;

    getInsEmailFinderData({
      need_verify: emailValidation,
      profile_count: profileCount,
      ins_email_finder_type: curTab.label,
      info: infoValue,
    })
      .then((res) => {
        if (!searchTabRef.current || searchTabRef.current !== curTab.tab) {
          return;
        }
        if (res.code == 0) {
          if (curTab.tab != 'followers' && curTab.tab != 'followings') {
            // 这里判断是不是隐私账号
            if (res.data.ins_media_acct?.private_mark == 1) {
              let PrivateData: InsAccountModel = {
                is_private: res.data.ins_media_acct?.private_mark ? 1 : 0,
                profile_pic_url: res.data.ins_media_acct?.profile_pic_url,
                media_name: res.data.ins_media_acct?.media_name,
              };
              setOpenPrivateAccount({
                title: t('OopsPrivateProfileDetected'),
                desc: (
                  <>
                    <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                      {t('ThisInstagramAccountIsPrivate_Desc')}
                    </div>
                    <UserDashboard {...PrivateData} />
                  </>
                ),
                btnTitle: t('TRYAGAIN'),
                btnDesc: (
                  <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
                    {t('Note: Only public accounts can be tracked.')}
                  </p>
                ),
              });
              setLoading(false);
              setAnimateLoading(false);
              return;
            } else if (res.data.ins_media_acct?.private_mark == 2) {
              setLoading(false);
              setAnimateLoading(false);
              setOpenPrivateAccount({
                title: t('Invalid Input'),
                desc: t('The content you entered is invalid. It may be in the wrong format, empty, or does not exist.'),
                btnTitle: t('Try Again'),
              });
              return;
            }
          }

          searchId.current = res.data.id;
          // 10秒后关闭 searching 动画
          const timer = setTimeout(() => {
            setAnimateLoading(false);
            clearTimeout(timer);
          }, 10000);
          pollingIntervalRef.current = setInterval(() => {
            getEmailFinderResult(res.data.id).then((resInner) => {
              if (!searchTabRef.current || searchTabRef.current !== curTab.tab) {
                return;
              }
              setResult(resInner.data);
              if (res.data.count / profileCount > 0.5) {
                setLoading(false);
              }
              if (resInner.data.status == 1 || resInner.data.status == -1) {
                setLoading(false);
                setAnimateLoading(false);
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }
                getUserCreditInfo();
              }
            });
          }, 2500);
          // const interval = setInterval(() => {
          //   getEmailFinderResult(res.data.id).then((resInner) => {
          //     setResult(resInner.data);
          //     if (res.data.count / profileCount > 0.5) {
          //       setLoading(false);
          //     }
          //     if (resInner.data.status == 1 || resInner.data.status == -1) {
          //       setLoading(false);
          //       setAnimateLoading(false);
          //       clearInterval(interval);
          //       getUserCreditInfo();
          //     }
          //   });
          // }, 2500);
        } else {
          setLoading(false);
          setAnimateLoading(false);
          setOpenPrivateAccount({
            title: t('Invalid Input'),
            desc: t('The content you entered is invalid. It may be in the wrong format, empty, or does not exist.'),
            btnTitle: t('Try Again'),
          });
        }
      })
      .catch((error) => {
        // 处理请求异常
        setLoading(false);
        setAnimateLoading(false);
        message.error(error?.message || 'Something went wrong, please try again later.');
      });
  };

  const rormRender = () => {
    if (currentTab === 'hashtag' || currentTab === 'location') {
      return (
        <InputSelect
          type={currentTab}
          placeholder={curTab?.placeholder}
          onSearch={(item) => {
            // 保存选中的 location 或 hashtag 信息
            // Location: 使用 pk，Hashtag: 使用 name
            const infoValue = item.type === 'location' ? item.detail?.pk : item.name;
            if (infoValue) {
              setSelectedLocationOrHashtag({
                pk: infoValue,
                name: item.name,
              });
              // 打开确认弹窗
              setOpenConfirmModal(true);
            }
          }}
          isLogin={isLogin}
          onTriggerLogin={() => onOpenLoginModal(true)}
        />
      );
    }

    return (
      <>
        <div className="flex items-center max-w-[610px] rounded-md overflow-hidden mx-auto pt-10">
          <Input
            className="flex-1 h-14"
            placeholder={t(curTab?.placeholder)}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                handleBtnClick();
              }
            }}
            prefix={<InstagramOutlined className="text-[22px] mr-1" />}
            suffix={
              <Button
                className={cn('h-[38px] primary-btn px-9 hidden lg:block')}
                type="primary"
                onClick={handleBtnClick}
                loading={loading}
              >
                {t('Find Email')}
              </Button>
            }
          />
        </div>
        <Button
          className={cn('h-[38px] max-w-[610px] mx-auto primary-btn px-9 block lg:hidden w-full mt-4')}
          type="primary"
          onClick={handleBtnClick}
          loading={loading}
        >
          {t('Find Email')}
        </Button>
      </>
    );
  };

  return (
    <div>
      <Hero
        data={sectionHero}
        currentTab={currentTab}
        pageType={type}
        setCurrentTabAction={(e) => {
          searchTabRef.current = null;
          setInputValue('');
          setSelectedLocationOrHashtag(null);
          setCurrentTab(e);
          setLoading(false);
          setAnimateLoading(false);
          setResult(null);
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }}
        userCreditInfo={userCreditInfo}
        isLogin={isLogin}
        guideToRegisterOrLogin={guideToRegisterOrLogin}
      >
        {rormRender()}
      </Hero>
      {currentTab === 'ai-discover' && (
        <AIDiscover
          prompts={emailFinderAIPrompts as PresetPrompt[]}
          userCreditInfo={userCreditInfo}
          onTaskIdChange={setAiDiscoverTaskId}
          initialTaskId={aiDiscoverInitialTaskId}
          isLogin={isLogin}
          onOpenLoginModal={() => onOpenLoginModal(true)}
        />
      )}
      {currentTab !== 'ai-discover' && (result || animateLoading) && (
        <ResultPeal
          data={result}
          loading={loading}
          searchId={searchId.current}
          animateLoading={animateLoading}
          currentTab={currentTab}
        />
      )}
      {type === pageTypeEnum.subPage && <StepCard data={sectionSteps} />}
      {type === pageTypeEnum.subPage && <ChooseContent data={sectionWhyChooseType} />}
      {type === pageTypeEnum.syntheticPage && <WhyChoose data={sectionWhyChoose} />}
      {type === pageTypeEnum.syntheticPage && <StepCard data={sectionSteps} />}
      <KeyFeatures data={sectionAllInOne} type={type} />
      {type === pageTypeEnum.subPage && <WhoBenefits data={sectionWhoBenefits} />}
      <PricingContent
        data={sectionPricing}
        isLogin={isLogin}
        guideToRegisterOrLogin={guideToRegisterOrLogin}
        userCreditInfo={userCreditInfo}
        currentTab={currentTab}
        aiDiscoverTaskId={aiDiscoverTaskId}
      />
      <div className="text-center max-w-[1260px] mx-auto  px-4 max-md:pt-16">
        <FAQContent faqInfo={sectionFAQ} faqList={faqList} />
        <BestTipsContent title={sectionTopBlog?.title} blogList={topBlogList} />
        <BestTipsContent title={sectionBlog?.title} blogList={blogList} />
      </div>
      <DownloadStoriesBottomContent
        bottomInfo={{
          title: sectionButtom?.title,
          desc: sectionButtom?.desc,
        }}
        placeholder={t(curTab?.placeholder)}
        buttonTitle={t('Find Email')}
        loading={false}
        searchVal={inputValue}
        inputVisible={true}
        onValueChanged={(e) => {
          setInputValue(e);
        }}
        onSearchClick={handleBtnClick}
      />
      {/* 确认弹窗 */}
      <OptModal
        open={openConfirmModal}
        onCancel={() => {
          setOpenConfirmModal(false);
        }}
        userCreditInfo={userCreditInfo}
        title={curTab?.modalTitle}
        description={curTab?.modalDesc}
        type={curTab?.tab}
        confirm={(emailValidation, profileCount) => {
          ga_logEvent(curTab.tagBuyName);
          handleConfirm(emailValidation, profileCount);
        }}
        userInfo={curTab?.tab == 'followings' || curTab?.tab == 'followers' ? userInfo : undefined}
      />
      {/* 输入错误弹窗 */}
      <CommonSimpleModal
        open={openPrivateAccount !== null}
        title={openPrivateAccount?.title}
        desc={openPrivateAccount?.desc}
        btnTitle={openPrivateAccount?.btnTitle}
        closeAction={(isOk: boolean) => {
          setOpenPrivateAccount(null);
          if (isOk) {
            setInputValue('');
          }
        }}
      />
    </div>
  );
}
